/**
 * hinterland_dressing.ts — geometry for the working landscape: crop rows and
 * fences on the field ring, orchard rows, the windmill (animated sails), the
 * walled burial ground, and shore quays. Companion to lib/hinterland.ts
 * (which decides WHERE everything goes) and settlement_dressing.ts (which
 * carries the shared materials + instancing helpers).
 *
 * All low-poly procedural instanced geometry; the module registers colliders/
 * solids/NPC seats from the returned records and spins the rotors per frame.
 */
import * as THREE from 'three/webgpu';
import type { SettlementSite } from '../../../shared/settlements.js';
import { meshHeight } from './terrain_stamp.js';
import { hinterlandWorks, cemeteryPlot, hs01 } from './hinterland.js';
import { civilizationOf } from './biome_settlements.js';
import { commonsSeaLevel } from './sea_level.js';
import {
  dressingMats, type DressingCollider, type DressingSolid, type DressingSeat, type PrefabPlacement,
} from './settlement_dressing.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);

function mat4(x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number): THREE.Matrix4 {
  _q.setFromAxisAngle(UP, yaw);
  return _m4.compose(_p.set(x, y, z), _q, _s.set(sx, sy, sz)).clone();
}

export interface HinterlandResult {
  group: THREE.Group;
  colliders: DressingCollider[];
  solids: DressingSolid[];
  seats: DressingSeat[];
  rotors: THREE.Object3D[];
  prefabs: PrefabPlacement[];
}

/** `prefabs`: the `prop:hinter_*` keys the catalogue carries — mapped parts become placements, the rest stays procedural. */
export function buildHinterlandDressing(seed: string, site: SettlementSite, prefabKeys?: ReadonlySet<string>): HinterlandResult {
  const M = dressingMats();
  const civ = civilizationOf(site.siteSeed);
  const clothMat = civ === 'a' ? M.clothA : M.clothB;
  const s = site.siteSeed;
  const group = new THREE.Group();
  group.name = `hinterland:${site.id}`;
  const colliders: DressingCollider[] = [];
  const solids: DressingSolid[] = [];
  const seats: DressingSeat[] = [];
  const rotors: THREE.Object3D[] = [];
  const prefabs: PrefabPlacement[] = [];
  const want = (k: string): boolean => !!prefabKeys?.has(k);
  const gy = (x: number, z: number): number => meshHeight(seed, x, z);
  const works = hinterlandWorks(seed, site);

  // Instanced accumulators.
  const acc = new Map<string, { geo: THREE.BufferGeometry; mat: THREE.Material; m: THREE.Matrix4[] }>();
  const add = (kind: string, geo: () => THREE.BufferGeometry, mat: THREE.Material, m4: THREE.Matrix4): void => {
    let b = acc.get(kind);
    if (!b) { b = { geo: geo(), mat, m: [] }; acc.set(kind, b); }
    b.m.push(m4);
  };

  // ── Field cells ─────────────────────────────────────────────────────────
  for (const c of works.cells) {
    const cos = Math.cos(c.yaw), sin = Math.sin(c.yaw);
    if (c.kind === 'crop') {
      // Crop rows: instanced low ridges of planted stalks following the cell's
      // long axis (the tilled-soil tint underneath comes from fieldW).
      const rows = 5;
      for (let r = 0; r < rows; r++) {
        const lz = -c.hd + 2 + (r / (rows - 1)) * (c.hd * 2 - 4);
        // Row growth phase varies per cell (roll) — young → tall → stubble.
        const growth = 0.35 + ((c.roll * 7 + r * 0.13) % 1) * 0.75;
        const segs = 4;
        for (let g = 0; g < segs; g++) {
          const lx = -c.hw + 2.5 + (g / (segs - 1)) * (c.hw * 2 - 5);
          const wx = c.x + lx * cos - lz * sin;
          const wz = c.z + lx * sin + lz * cos;
          const y = gy(wx, wz);
          if (want('prop:hinter_crop_row')) prefabs.push({ key: 'prop:hinter_crop_row', x: wx, y, z: wz, yaw: c.yaw, len: 5.6, hMul: 0.45 + growth * 0.7 });
          else add('croprow', () => new THREE.BoxGeometry(5.6, 1, 0.5), M.leafGreen,
            mat4(wx, y + growth * 0.4, wz, c.yaw, 1, growth * 0.8, 1));
        }
      }
    } else if (c.kind === 'orchard') {
      // Orchard: a small grid of round-canopy trees.
      for (let r = 0; r < 2; r++) {
        for (let g2 = 0; g2 < 4; g2++) {
          const lx = -c.hw + 4 + g2 * ((c.hw * 2 - 8) / 3);
          const lz = -c.hd + 5 + r * (c.hd * 2 - 10);
          const wx = c.x + lx * cos - lz * sin;
          const wz = c.z + lx * sin + lz * cos;
          const y = gy(wx, wz);
          const sc = 0.8 + hs01(s, `orch${Math.round(wx)},${Math.round(wz)}`) * 0.4;
          if (want('prop:hinter_orchard_tree')) prefabs.push({ key: 'prop:hinter_orchard_tree', x: wx, y, z: wz, yaw: c.roll * 6 + g2 * 1.7, targetH: 3.5 * sc, collide: true });
          else {
          add('orchtrunk', () => new THREE.CylinderGeometry(0.12, 0.17, 1.6, 5), M.woodDark, mat4(wx, y + 0.8 * sc, wz, 0, sc, sc, sc));
          add('orchcanopy', () => new THREE.IcosahedronGeometry(1.25, 0), M.leafGreen, mat4(wx, y + 2.3 * sc, wz, c.roll * 6, sc, sc * 0.9, sc));
          }
          seats.push({ x: wx, z: wz, r: 0.6 });
        }
      }
    } else {
      // Pasture: a post-and-rail fence around the cell.
      const fenceProp = want('prop:hinter_fence');
      const perim: Array<[number, number]> = [];
      const step = 4;
      for (let t = -c.hw; t < c.hw; t += step) perim.push([t, -c.hd], [t, c.hd]);
      for (let t = -c.hd; t < c.hd; t += step) perim.push([-c.hw, t], [c.hw, t]);
      let iP = 0;
      for (const [lx, lz] of perim) {
        if (fenceProp) break; // the generated segment carries its own posts
        const wx = c.x + lx * cos - lz * sin;
        const wz = c.z + lx * sin + lz * cos;
        const y = gy(wx, wz);
        add('fpost', () => new THREE.BoxGeometry(0.14, 1.2, 0.14), M.woodDark, mat4(wx, y + 0.6, wz, c.yaw, 1, 1, 1));
        // Rails along the long edges (paired posts step along the same edge).
        if (iP % 2 === 0 && Math.abs(lz + c.hd) < 0.01) { /* rails drawn as spans below */ }
        iP++;
      }
      // Rails: four straight spans (visual shortcut — posts carry the read).
      for (const [ex, ez, eyaw, len] of [
        [0, -c.hd, c.yaw, c.hw * 2], [0, c.hd, c.yaw, c.hw * 2],
        [-c.hw, 0, c.yaw + Math.PI / 2, c.hd * 2], [c.hw, 0, c.yaw + Math.PI / 2, c.hd * 2],
      ] as Array<[number, number, number, number]>) {
        const wx = c.x + ex * cos - ez * sin;
        const wz = c.z + ex * sin + ez * cos;
        const y = gy(wx, wz);
        if (fenceProp) {
          // Generated fence: N segments of ~4.8 m laid along the span (local X of the prop = the rail
          // direction). 4.8 m, not 2.4: the cooked segment is seam-bound at ~10k tris and a town's field
          // ring took 924 segments — half the instances is the lever the cooker cannot give.
          const n = Math.max(1, Math.round(len / 4.8)), L = len / n;
          const dx = Math.cos(eyaw), dz = -Math.sin(eyaw);
          for (let i = 0; i < n; i++) {
            const t = -len / 2 + (i + 0.5) * L;
            const sx = wx + dx * t, sz = wz + dz * t;
            prefabs.push({ key: 'prop:hinter_fence', x: sx, y: gy(sx, sz), z: sz, yaw: eyaw, len: L, collide: true });
          }
        } else add('frail', () => new THREE.BoxGeometry(1, 0.09, 0.07), M.wood, mat4(wx, y + 0.95, wz, eyaw, len, 1, 1));
      }
    }
  }

  // ── The windmill ────────────────────────────────────────────────────────
  if (works.windmill) {
    const w = works.windmill;
    const y = gy(w.x, w.z);
    const towerH = 8.5;
    if (want('prop:hinter_mill_tower')) prefabs.push({ key: 'prop:hinter_mill_tower', x: w.x, y, z: w.z, yaw: w.yaw, targetH: towerH + 1.6 });
    else {
    add('milltower', () => new THREE.CylinderGeometry(1.4, 2.1, towerH, 8), civ === 'a' ? M.wallA : M.wallB, mat4(w.x, y + towerH / 2, w.z, w.yaw, 1, 1, 1));
    add('millcap', () => new THREE.ConeGeometry(1.7, 1.6, 8), M.woodDark, mat4(w.x, y + towerH + 0.7, w.z, w.yaw, 1, 1, 1));
    }
    if (want('prop:hinter_mill_rotor')) prefabs.push({ key: 'prop:hinter_mill_rotor', x: w.x + Math.sin(w.yaw) * 1.9, y: y + towerH - 0.6, z: w.z + Math.cos(w.yaw) * 1.9, yaw: w.yaw, targetH: 9.6, rotor: true });
    else {
    // The rotor: 4 sail planes on a hub, facing the yaw; spun by the module.
    const rotor = new THREE.Group();
    rotor.position.set(w.x + Math.sin(w.yaw) * 1.9, y + towerH - 0.6, w.z + Math.cos(w.yaw) * 1.9);
    rotor.rotation.y = w.yaw;
    for (let i = 0; i < 4; i++) {
      const sail = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 4.6), M.clothWhite);
      sail.position.y = 2.5;
      const arm = new THREE.Group();
      arm.add(sail);
      arm.rotation.z = (i / 4) * Math.PI * 2;
      rotor.add(arm);
    }
    group.add(rotor);
    rotors.push(rotor);
    }
    colliders.push({ hx: 1.8, hy: towerH / 2, hz: 1.8, x: w.x, y: y + towerH / 2, z: w.z, yaw: w.yaw });
    solids.push({ x: w.x, z: w.z, hx: 2.0, hz: 2.0, yaw: w.yaw, base: y, top: y + towerH + 2 });
    seats.push({ x: w.x, z: w.z, r: 2.4 });
  }

  // ── The burial ground ───────────────────────────────────────────────────
  if (works.cemetery) {
    const c = works.cemetery;
    const y0 = gy(c.x, c.z);
    const cos = Math.cos(c.yaw), sin = Math.sin(c.yaw);
    // Low wall with a front gate gap; posts at corners.
    const wallH = 1.1;
    const segs: Array<[number, number, number, number]> = [];
    // Front edge (toward -hd local z) carries the gate gap at center.
    segs.push([-c.hw / 2 - 1.4, -c.hd, c.yaw, c.hw - 2.8]);
    segs.push([c.hw / 2 + 1.4, -c.hd, c.yaw, c.hw - 2.8]);
    segs.push([0, c.hd, c.yaw, c.hw * 2]);
    segs.push([-c.hw, 0, c.yaw + Math.PI / 2, c.hd * 2]);
    segs.push([c.hw, 0, c.yaw + Math.PI / 2, c.hd * 2]);
    for (const [lx, lz, wyaw, len] of segs) {
      const wx = c.x + lx * cos - lz * sin;
      const wz = c.z + lx * sin + lz * cos;
      const y = gy(wx, wz);
      add('cemwall', () => new THREE.BoxGeometry(1, 1, 1), M.stone, mat4(wx, y + wallH / 2, wz, wyaw, len, wallH, 0.4));
      colliders.push({ hx: len / 2, hy: wallH / 2, hz: 0.2, x: wx, y: y + wallH / 2, z: wz, yaw: wyaw });
    }
    // Gate arch: two posts + lintel at the front-center.
    const gx = c.x + 0 * cos - -c.hd * sin;
    const gz = c.z + 0 * sin + -c.hd * cos;
    const gyv = gy(gx, gz);
    if (want('prop:hinter_cem_gate')) prefabs.push({ key: 'prop:hinter_cem_gate', x: gx, y: gyv, z: gz, yaw: c.yaw, targetH: 3.1 });
    else {
    for (const sgn of [-1, 1]) {
      add('cempost', () => new THREE.BoxGeometry(0.35, 2.6, 0.35), M.stoneDark,
        mat4(gx + Math.cos(c.yaw) * 1.5 * sgn, gyv + 1.3, gz + Math.sin(c.yaw) * 1.5 * sgn, c.yaw, 1, 1, 1));
    }
    add('cemlintel', () => new THREE.BoxGeometry(3.6, 0.35, 0.45), M.stoneDark, mat4(gx, gyv + 2.7, gz, c.yaw, 1, 1, 1));
    }
    // Cypress-analog sentinels: tall dark cones at the rear corners.
    for (const sgn of [-1, 1]) {
      const tx = c.x + (c.hw - 1.5) * sgn * cos - (c.hd - 1.5) * sin;
      const tz = c.z + (c.hw - 1.5) * sgn * sin + (c.hd - 1.5) * cos;
      const ty = gy(tx, tz);
      if (want('prop:hinter_cypress')) prefabs.push({ key: 'prop:hinter_cypress', x: tx, y: ty, z: tz, yaw: sgn * 0.8, targetH: 6.0, collide: true });
      else {
      add('cypress', () => new THREE.ConeGeometry(0.85, 5.2, 6), M.leafDark, mat4(tx, ty + 2.6, tz, 0, 1, 1, 1));
      add('cyptrunk', () => new THREE.CylinderGeometry(0.12, 0.16, 0.8, 5), M.woodDark, mat4(tx, ty + 0.4, tz, 0, 1, 1, 1));
      }
    }
    // Two mourning benches inside, facing the rows.
    for (const sgn of [-1, 1]) {
      const bx = c.x + (c.hw * 0.4) * sgn * cos - (-c.hd + 2.2) * sin;
      const bz = c.z + (c.hw * 0.4) * sgn * sin + (-c.hd + 2.2) * cos;
      if (want('prop:hinter_bench')) prefabs.push({ key: 'prop:hinter_bench', x: bx, y: gy(bx, bz), z: bz, yaw: c.yaw, len: 1.6, collide: true });
      else add('cembench', () => new THREE.BoxGeometry(1.5, 0.42, 0.45), M.wood, mat4(bx, gy(bx, bz) + 0.35, bz, c.yaw, 1, 1, 1));
    }
    // Empty-plot mounds: the precinct reads as a burial ground even before
    // the server's grave rows fill the rows (graves render via AtlasGraves).
    const mounds = Math.min(6, 2 + Math.floor(hs01(s, 'mounds') * 5));
    for (let i = 0; i < mounds; i++) {
      const p = cemeteryPlot(seed, site, i * 7 + 3);
      if (!p) continue;
      if (want('prop:hinter_grave_marker')) prefabs.push({ key: 'prop:hinter_grave_marker', x: p.x, y: gy(p.x, p.z), z: p.z, yaw: c.yaw + hs01(s, `mnd${i}`) * 0.3, targetH: 1.1 });
      else add('mound', () => new THREE.SphereGeometry(0.6, 6, 4), M.soil, mat4(p.x, gy(p.x, p.z) - 0.28, p.z, hs01(s, `mnd${i}`) * 3, 1.2, 0.55, 0.8));
    }
    seats.push({ x: gx, z: gz, r: 1.2 });
    solids.push({ x: c.x, z: c.z, hx: c.hw, hz: c.hd, yaw: c.yaw, base: y0 - 1, top: y0 + 1.4 });
  }

  // ── Shore quays ─────────────────────────────────────────────────────────
  const sea = commonsSeaLevel(seed);
  for (const q of works.quays) {
    // Boardwalk running from the shore point out over the water along yaw.
    const deckY = sea + 0.9;
    const segN = 3;
    for (let i = 0; i < segN; i++) {
      const t = (i + 0.5) / segN;
      const wx = q.x + Math.cos(q.yaw) * q.len * t;
      const wz = q.z + Math.sin(q.yaw) * q.len * t;
      add('quaydeck', () => new THREE.BoxGeometry(1, 0.28, 4.2), M.wood, mat4(wx, deckY, wz, q.yaw + Math.PI / 2, q.len / segN + 0.15, 1, 1));
      colliders.push({ hx: q.len / segN / 2 + 0.1, hy: 0.15, hz: 2.1, x: wx, y: deckY, z: wz, yaw: q.yaw + Math.PI / 2 });
      for (const sgn of [-1, 1]) {
        add('quaypile', () => new THREE.CylinderGeometry(0.16, 0.18, 3.4, 6), M.woodDark,
          mat4(wx + Math.cos(q.yaw + Math.PI / 2) * 1.9 * sgn, deckY - 1.4, wz + Math.sin(q.yaw + Math.PI / 2) * 1.9 * sgn, 0, 1, 1, 1));
      }
    }
    // Net rack + a moored skiff.
    const rx = q.x - Math.cos(q.yaw) * 4, rz = q.z - Math.sin(q.yaw) * 4;
    const ry = gy(rx, rz);
    if (want('prop:hinter_net_rack')) prefabs.push({ key: 'prop:hinter_net_rack', x: rx, y: ry, z: rz, yaw: q.yaw + Math.PI / 2, len: 2.6, collide: true });
    else {
    add('netrack', () => new THREE.BoxGeometry(2.6, 1.5, 0.12), M.woodDark, mat4(rx, ry + 0.75, rz, q.yaw + Math.PI / 2, 1, 1, 1));
    const net = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.1), clothMat);
    net.position.set(rx, ry + 0.8, rz);
    net.rotation.y = q.yaw + Math.PI / 2;
    group.add(net);
    }
    const bx = q.x + Math.cos(q.yaw) * (q.len + 4) + Math.cos(q.yaw + Math.PI / 2) * 3;
    const bz = q.z + Math.sin(q.yaw) * (q.len + 4) + Math.sin(q.yaw + Math.PI / 2) * 3;
    if (want('prop:hinter_skiff')) prefabs.push({ key: 'prop:hinter_skiff', x: bx, y: sea - 0.1, z: bz, yaw: -q.yaw, len: 4.2, collide: true });
    else add('skiff', () => new THREE.CapsuleGeometry(0.8, 2.4, 3, 6), M.woodDark, ((): THREE.Matrix4 => {
      _q.setFromAxisAngle(new THREE.Vector3(Math.sin(q.yaw + Math.PI / 2), 0, Math.cos(q.yaw + Math.PI / 2)), Math.PI / 2);
      return _m4.compose(_p.set(bx, sea + 0.15, bz), _q, _s.set(1, 0.5, 0.9)).clone();
    })());
    seats.push({ x: q.x, z: q.z, r: 2.0 });
  }

  for (const b of acc.values()) {
    pinStaticMotionVectors(b.mat); // static props: zero object velocity (three's frozen previous instance matrices)
    const im = new THREE.InstancedMesh(b.geo, b.mat, b.m.length);
    for (let i = 0; i < b.m.length; i++) im.setMatrixAt(i, b.m[i]);
    im.instanceMatrix.needsUpdate = true;
    im.receiveShadow = true;
    group.add(im);
  }
  return { group, colliders, solids, seats, rotors, prefabs };
}
